import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { AppDatabase } from '@/integrations/supabase/app-types';
import { supabase } from '@/integrations/supabase/client';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { logger } from '@/lib/logger';

interface TimezoneContextType {
  timezone: string;
  setTimezone: (timezone: string) => void;
  formatDate: (date: string | Date, formatStr?: string) => string;
  formatDateSafe: (dateString: string | null, formatStr?: string) => string;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

interface TimezoneProviderProps {
  children: ReactNode;
}

export const TimezoneProvider = ({ children }: TimezoneProviderProps) => {
  const { user } = useAuth();
  const [timezone, setTimezoneState] = useState<string>('America/New_York');

  // Load user's timezone preference on mount
  useEffect(() => {
    if (!user) {
      // For logged out users, default to Eastern Time
      setTimezoneState('America/New_York');
      return;
    }

    let isCurrent = true;

    const loadUserTimezone = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('timezone')
          .eq('id', user.id)
          .single();

        if (!isCurrent) return;

        if (error) {
          logger.warn('Failed to load timezone preference:', error);
          setTimezoneState('America/New_York');
          return;
        }

        const profile = data as { timezone?: string | null } | null;
        setTimezoneState(profile?.timezone || 'America/New_York');
      } catch (error) {
        if (!isCurrent) return;

        logger.warn('Error loading timezone:', error);
        setTimezoneState('America/New_York');
      }
    };

    void loadUserTimezone();

    return () => {
      isCurrent = false;
    };
  }, [user]);

  const setTimezone = async (newTimezone: string) => {
    setTimezoneState(newTimezone);

    // Save to database if user is logged in
    if (user) {
      try {
        const profileUpdate: AppDatabase['public']['Tables']['profiles']['Update'] = {
          timezone: newTimezone,
        };

        const { error } = await supabase
          .from('profiles')
          .update(profileUpdate as never)
          .eq('id', user.id);

        if (error) {
          logger.error('Failed to save timezone preference:', error);
        }
      } catch (error) {
        logger.error('Error saving timezone:', error);
      }
    }
  };

  const formatDate = (date: string | Date, formatStr: string = 'MMM d, yyyy h:mm a'): string => {
    try {
      if (typeof date === 'string') {
        // Check if this looks like a FormAssembly date (YYYY-MM-DD HH:MM:SS without timezone)
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date)) {
          logger.debug('🔍 FormAssembly date detected:', date, 'User timezone:', timezone);

          // For now, let's assume FormAssembly dates are in UTC and just display them in user's timezone
          // Convert "2025-09-24 14:53:27" to ISO format "2025-09-24T14:53:27Z"
          const isoString = date.replace(' ', 'T') + 'Z';
          const dateObj = new Date(isoString);
          const result = formatInTimeZone(dateObj, timezone, formatStr);

          logger.debug('🔍 Converted:', isoString, '->', result);
          return result;
        } else {
          // If it's an ISO string (from our database), parse as UTC
          const dateObj = parseISO(date);
          return formatInTimeZone(dateObj, timezone, formatStr);
        }
      } else {
        return formatInTimeZone(date, timezone, formatStr);
      }
    } catch (error) {
      logger.warn('Error formatting date:', error, { date, timezone, formatStr });
      return 'Invalid date';
    }
  };

  const formatDateSafe = (dateString: string | null, formatStr: string = 'MMM d, yyyy h:mm a'): string => {
    if (!dateString) {
      return 'N/A';
    }

    try {
      // Use the same logic as formatDate for consistency
      return formatDate(dateString, formatStr);
    } catch (error) {
      logger.warn('Error in formatDateSafe:', error, { dateString, timezone });
      return 'N/A';
    }
  };

  return (
    <TimezoneContext.Provider
      value={{
        timezone,
        setTimezone,
        formatDate,
        formatDateSafe,
      }}
    >
      {children}
    </TimezoneContext.Provider>
  );
};
